import axios from 'axios';
import { cachedGet } from '@/utils/cache';
import { API_BASE_URL } from '../config';

interface TradingStatistics {
  total_pnl?: number;
  pnl?: number;
  pnl_today?: number;
  monthly_rate?: number;
  win_rate?: number;
  total_trades?: number;
  monthly_avg_pnl_per_trade?: number;
  [key: string]: unknown;
}

interface TradingStatisticsApiResponse {
  statistics: TradingStatistics;
}

// Configure axios base URL
axios.defaults.baseURL = API_BASE_URL;

// Axios interceptor to attach access_token to every request if present
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      
      // Only log in development and never log the actual token
      if (process.env.NODE_ENV === 'development') {
        console.log(`[API] Making ${config.method?.toUpperCase()} request to:`, config.url);
        console.log('[API] Authorization header present:', !!token);
      }
    } else if (process.env.NODE_ENV === 'development') {
      console.warn('[API] No access token found in localStorage');
    }
    return config;
  },
  (error) => {
    console.error('[API] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Dashboard stats (balance, pnl, monthly rate, win rate)
export async function getDashboardStats() {
  const res = await axios.get("/api/trading/dashboard_stats");
  return res.data;
}

// Type definitions for batch response
export interface BatchMetaData {
  generated_at: string;
  trading_mode: 'paper' | 'live';
  symbol: string;
  timeframe: string;
  has_errors: boolean;
}

export interface DashboardMarketPrice {
  symbol: string;
  price?: number;
  mark_price?: number;
  markPrice?: number;
  price_source?: string;
  change_24h?: number;
  change_percent_24h?: number;
  high_24h?: number;
  low_24h?: number;
  volume_24h?: number;
  quote_volume_24h?: number;
  last_price?: number;
  timestamp?: number;
  last_updated?: string;
  is_stale?: boolean;
  stale_age_ms?: number;
  error?: string | {
    code: string;
    message?: string;
  };
}

export interface BatchResponseData {
  balance?: {
    balance: number | string;
    currency?: string;
    is_paper?: boolean;
    is_stale?: boolean;
    error?: string | {
      code: string;
      message?: string;
    };
  };
  statistics?: TradingStatistics & {
    total_pnl?: number;
    total_pnl_value?: number;
    pnl_value?: number;
    win_rate?: number;
    monthly_avg_pnl_per_trade?: number;
    total_trades?: number;
    error?: string | {
      code: string;
      message?: string;
    };
  };
  positions?: {
    app_positions?: any[];
    external_positions?: any[];
    paper_positions?: any[];
    positions?: any[];
    count?: number;
    position_incidents?: any[];
    exchange_sync_status?: {
      healthy?: boolean;
      incident_count?: number;
      incidents?: any[];
    };
    error?: string | {
      code: string;
      message?: string;
    };
  };
  forecast?: any & {
    error?: string | {
      code: string;
      message?: string;
    };
  };
  risk_settings?: {
    current_tier: 1 | 2 | 3;
    max_leverage_for_tier: number;
    max_leverage_for_exchange: number;
    investment_percentage: number;
    leverage: number;
    max_stop_loss_usd?: number | null;
    exchange: string;
    account_type: string;
    is_configured: boolean;
    grid_enabled?: boolean;
    grid_levels?: number;
    grid_sizing_mode?: string;
    grid_scale_multiplier?: number;
    grid_strategy_mode?: string;
    grid_lower_price?: number | null;
    grid_upper_price?: number | null;
    grid_recommendation_meta?: any;
    paper_grid_strategy_mode?: string;
    paper_grid_lower_price?: number | null;
    paper_grid_upper_price?: number | null;
    error?: string | {
      code: string;
      message?: string;
    };
  };
  sltp_stats?: {
    stats: any;
    error?: string | {
      code: string;
      message?: string;
    };
  };
  market_price?: DashboardMarketPrice;
  grid_metrics?: {
    success: boolean;
    grid: any | null;
    error?: string;
  };
  signal_status?: {
    system_status?: string;
    latest_broadcast?: any;
    cached_signals?: Record<string, any>;
    cached_signals_count?: number;
    message?: string | null;
    error?: string | {
      code: string;
      message?: string;
    };
  };
  auto_trading_status?: {
    auto_trading_enabled?: boolean;
    paper_auto_trading_enabled?: boolean;
    paper_trading_mode?: boolean;
    user_id?: number;
    error?: string | {
      code: string;
      message?: string;
    };
  };
  auto_trading_notifications?: {
    notifications?: Array<{
      id: string;
      reason: string;
      timestamp: string;
      risk_level: string;
    }>;
    count?: number;
    error?: string | {
      code: string;
      message?: string;
    };
  };
  /** @deprecated The runtime no longer returns this strategy-only section. */
  ai_reasoning?: {
    strategy_enabled?: boolean;
    provider?: string | null;
    provider_label?: string | null;
    model?: string | null;
    config_source?: string;
    prompt_source?: string;
    is_ready?: boolean;
    error?: string | { code: string; message?: string };
  };
  market_news?: {
    status?: 'ready' | 'warming';
    message?: string;
    symbol?: string;
    timeframe?: string;
    generated_at?: string;
    candle_open_at?: string;
    candle_close_at?: string;
    next_refresh_at?: string;
    asset_news?: Array<{
      title: string;
      source?: string;
      url?: string;
      published_at?: string;
      summary?: string;
    }>;
    macro_news?: Array<{
      title: string;
      source?: string;
      url?: string;
      published_at?: string;
      summary?: string;
    }>;
    error?: string | {
      code: string;
      message?: string;
    };
  };
  meta: BatchMetaData;
}

/**
 * Fetches all dashboard data in a single batch request
 * 
 * @param {string} [symbol] - The trading pair symbol (e.g., 'BTCUSDT')
 * @param {string} [timeframe] - The chart timeframe (e.g., '1h', '4h', '1d')
 * @param {Object} [options] - Cache options for the request
 * @param {number} [options.ttl=30000] - Time to live for the cache in milliseconds (default: 30000)
 * @param {number} [options.debounce=0] - Debounce time in milliseconds (default: 0)
 * @returns {Promise<BatchResponseData>} Promise that resolves to the batch response data
 * 
 * @example
 * // Basic usage
 * const batchData = await getDashboardBatch();
 * 
 * @example
 * // With symbol and timeframe
 * const batchData = await getDashboardBatch('BTCUSDT', '1h');
 */
export async function getDashboardBatch(
  symbol?: string,
  timeframe?: string,
  options?: import('@/utils/cache').CacheOptions & { forceRefresh?: boolean; include?: string[] }
): Promise<BatchResponseData> {
  // Build query parameters if symbol or timeframe is provided
  const params = new URLSearchParams();
  if (symbol) params.append('symbol', symbol);
  if (timeframe) params.append('timeframe', timeframe);
  
  if (options?.forceRefresh) {
    params.append('force_refresh', '1');
  }
  if (options?.include?.length) {
    params.append('include', options.include.join(','));
  }

  const queryString = params.toString();
  const url = queryString ? `/api/dashboard/batch?${queryString}` : '/api/dashboard/batch';
  
  try {
    const response = await cachedGet<BatchResponseData>(url, {
      ttl: 30000, // 30 seconds cache
      debounce: 300, // Consolidate rapid refreshes
      forceRefresh: options?.forceRefresh, // Allow callers to bypass cache when needed
      ...options
    });

    // Log any partial failures
    if (response.meta?.has_errors) {
      console.warn('[getDashboardBatch] Partial batch failure detected', {
        balanceError: response.balance?.error,
        statisticsError: response.statistics?.error,
        positionsError: (response.positions as any)?.error,
        riskSettingsError: response.risk_settings?.error,
        sltpStatsError: response.sltp_stats?.error,
        autoTradingStatusError: response.auto_trading_status?.error,
        autoTradingNotificationsError: response.auto_trading_notifications?.error
      });
    }

    return response;
  } catch (error) {
    console.error('[getDashboardBatch] Error fetching batch data:', error);
    // Return a minimal valid response with error state
    return {
      meta: {
        generated_at: new Date().toISOString(),
        trading_mode: 'live',
        symbol: symbol || '',
        timeframe: timeframe || '1h',
        has_errors: true
      },
      balance: {
        balance: 0,
        error: {
          code: 'BATCH_FETCH_ERROR',
          message: 'Failed to fetch batch data'
        }
      }
    };
  }
}

// Fetch trading statistics (PnL, Monthly Rate, Win Rate)
export async function getTradingStatistics(options?: import('@/utils/cache').CacheOptions) {
  try {
    const data = await cachedGet<TradingStatisticsApiResponse>('/api/trading/statistics', { ttl: 300000, ...options });
    console.log('[getTradingStatistics] Success: cached response');
    return data.statistics;
  } catch (error: any) {
    console.error('[getTradingStatistics] Error:', error);
    throw error;
  }
}

// Fetch real balance from user's API credentials
export async function getRealBalance(options?: import('@/utils/cache').CacheOptions & { fast?: boolean }) {
  try {
    const params = new URLSearchParams();
    if (options?.forceRefresh) {
      params.append('force_refresh', '1');
    }
    if (options?.fast) {
      params.append('fast', '1');
    }

    const queryString = params.toString();
    const url = queryString ? `/api/trading/real-balance?${queryString}` : '/api/trading/real-balance';

    const data = await cachedGet(url, { ttl: 60000, ...options });
    console.log('[getRealBalance] Success: cached response');
    return data;
  } catch (error: any) {
    console.error('[getRealBalance] Error:', error);
    throw error;
  }
}

// Active open trading position
export async function getActivePosition(options?: import('@/utils/cache').CacheOptions) {
  try {
    const data = await cachedGet('/api/trading/active_position', { ttl: 30000, ...options });
    console.log('[getActivePosition] Success: cached response');
    return data;
  } catch (error: any) {
    console.error('[getActivePosition] Error:', error);
    throw error;
  }
}

// Paginated trading history
export async function getTradingHistory(page = 1) {
  try {
    const res = await axios.get(`/api/trading/history?page=${page}`);
    console.log('[getTradingHistory] Success:', res.status, res.headers['content-type']);
    return res.data.trades || [];
  } catch (error: any) {
    console.error('[getTradingHistory] Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      contentType: error.response?.headers?.['content-type'],
      data: typeof error.response?.data === 'string' ? error.response.data.substring(0, 200) : error.response?.data
    });
    throw error;
  }
}

// Risk settings API calls
export async function getRiskSettings() {
  const res = await axios.get("/api/trading/risk-settings");
  return res.data;
}

export async function saveRiskSettings(settings: {
  investment_percentage: number;
  leverage: number;
  max_stop_loss_usd?: number | null;
  exchange: string;
  account_type: string;
  grid_enabled?: boolean;
  grid_levels?: number;
  grid_sizing_mode?: 'equal' | 'scaled' | string;
  grid_scale_multiplier?: number;
  grid_strategy_mode?: 'manual' | 'ai_auto' | string;
  grid_lower_price?: number | null;
  grid_upper_price?: number | null;
  grid_recommendation_meta?: any;
  trailing_stop_enabled?: boolean;
  selected_trading_symbols?: string[] | null;
}) {
  const res = await axios.post("/api/trading/risk-settings", settings);
  return res.data;
}

export async function recommendGridStrategy(symbol: string = 'BTCUSDT', timeframe: string = '1h', isPaper?: boolean) {
  const res = await axios.post('/api/trading/grid/recommend', { symbol, timeframe, is_paper: isPaper });
  return res.data;
}

export async function executeGrid(symbol: string = 'BTCUSDT', isPaper?: boolean) {
  const res = await axios.post('/api/trading/grid/execute', { symbol, is_paper: isPaper });
  return res.data;
}

export async function validateRiskSettings(settings: {
  investment_percentage: number;
  leverage: number;
  exchange: string;
  account_type: string;
}) {
  const res = await axios.post("/api/trading/risk-settings/validate", settings);
  return res.data;
}

export async function refreshMarketNews(symbol?: string, timeframe?: string) {
  const params = new URLSearchParams();
  if (symbol) params.append('symbol', symbol);
  if (timeframe) params.append('timeframe', timeframe);
  const qs = params.toString();
  const url = qs ? `/api/dashboard/news/refresh?${qs}` : '/api/dashboard/news/refresh';
  const res = await axios.get(url);
  return res.data;
}

export default axios;
